import styled from 'styled-components';
import { allProps, Box, Colors, CustomTheme, Sizes } from '@altermaind/web/ui';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';

type StepIndicatorProps = {
  isFirst: boolean;
  isLast: boolean;
  circleSize: keyof Sizes;
  lineColor: keyof Colors;
  hasOnlyOneStep?: boolean;
};

export const StepIndicator = styled.div<StepIndicatorProps>(
  ({ theme, isFirst, isLast, circleSize, lineColor, hasOnlyOneStep }) => {
    const indicatorColor = convertColorToRgba(
      (theme.colors as keyof CustomTheme['colors'])[lineColor],
      1,
    );
    const convertedSize = convertSizeToNumber(theme?.space[circleSize]) || 0;
    if (hasOnlyOneStep) {
      return `
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: center;
      width: ${convertedSize}px;
      min-width: ${convertedSize}px;
    `;
    }

    return `
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: ${convertedSize}px;
      min-width: ${convertedSize}px;

      &::after {
        content: '';
        display: ${isLast ? 'none' : 'block'};
        position: absolute;
        top: 50%;
        left: 50%;
        width: 1px;
        height: ${isFirst ? 'calc(100% + 18px)' : '100%'};
        background: ${indicatorColor};
        transform: translateX(-50%);
      }

      &::before {
        content: '';
        display: ${isLast ? 'block' : 'none'};
        position: absolute;
        top: 0;
        bottom: 50%;
        left: 50%;
        width: 1px;
        height: 50%;
        background: ${indicatorColor};
        transform: translateX(-50%);
      }
    `;
  },
  allProps,
);

type StepCircleProps = {
  size: keyof Sizes;
  bg: keyof Colors;
  color: keyof Colors;
};

export const StepCircle = styled(Box)<StepCircleProps>(({ theme, size, bg }) => {
  const convertedSize = convertSizeToNumber(theme?.space[size]) || 0;
  const backgroundColor = convertColorToRgba((theme.colors as keyof CustomTheme['colors'])[bg], 1);
  return `
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${convertedSize}px;
      height: ${convertedSize}px;
      border-radius: 50%;
      font-weight: 600;
      z-index: 1;
      border: 2px solid ${backgroundColor};
      font-family: 'Helvetica';
    `;
}, allProps);

StepCircle.defaultProps = {
  bg: 'primary/text',
  color: 'primary/interactive',
  fontSize: 's',
};
